import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Calculator, IndianRupee, ChartPie, Package, Target, RotateCcw, Copy, Share2 } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";
import { AdUnit } from "@/components/AdUnit";

const DEFAULTS = {
  price: "299", cost: "75", packaging: "10", shipping: "45",
  feeMode: "pct", feePct: "10", feeFixed: "0",
  ads: "15", gst: "0", other: "0", orders: "1", target: "",
};

const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n);
const pct = (n) => `${n.toFixed(1)}%`;

function num(v) {
  if (v === "" || v === null || v === undefined) return null;
  const n = parseFloat(v);
  return Number.isNaN(n) ? null : n;
}

function validate(name, v) {
  if (v === "") return null;
  const n = num(v);
  if (n === null) return "Enter a number";
  if (n < 0) return "Must be 0 or more";
  if ((name === "feePct" || name === "gst") && n > 100) return "Cannot exceed 100";
  if (name === "orders" && (n < 1 || !Number.isInteger(n))) return "Enter at least 1 whole order";
  return null;
}

const JSONLD = [{
  "@context": "https://schema.org",
  "@type": "WebApplication",
  name: "E-commerce Profit Calculator",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Any",
  offers: { "@type": "Offer", price: "0", priceCurrency: "INR" },
  description: "Free e-commerce profit calculator for marketplace sellers.",
  url: "https://ecroplyai.in/profit-calculator",
}];

function Field({ label, name, value, onChange, prefix, suffix, error, testid, step = "any", inputMode = "decimal", placeholder }) {
  return (
    <div>
      <label htmlFor={testid} className="block text-sm font-semibold text-slate-800 mb-1.5">{label}</label>
      <div className="relative">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">{prefix}</span>}
        <input
          id={testid} data-testid={testid}
          type="number" inputMode={inputMode} min="0" step={step} placeholder={placeholder}
          value={value} onChange={(e) => onChange(name, e.target.value)}
          className={`w-full h-11 rounded-xl border bg-slate-50 text-base text-slate-900 px-3 ${prefix ? "pl-8" : ""} ${suffix ? "pr-8" : ""} focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${error ? "border-red-400 bg-red-50" : "border-slate-200"}`}
        />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">{suffix}</span>}
      </div>
      {error && <p className="text-xs text-red-600 mt-1" data-testid={`${testid}-error`}>{error}</p>}
    </div>
  );
}

export default function ProfitCalculator() {
  const [v, setV] = useState(DEFAULTS);
  const set = (k, val) => setV((o) => ({ ...o, [k]: val }));

  const errors = useMemo(() => {
    const e = {};
    for (const k of ["price", "cost", "packaging", "shipping", "feePct", "feeFixed", "ads", "gst", "other", "orders", "target"]) {
      const err = validate(k, v[k]);
      if (err) e[k] = err;
    }
    return e;
  }, [v]);

  const r = useMemo(() => {
    const price = num(v.price);
    const ready = price !== null && Object.keys(errors).length === 0;
    if (!ready) return { ready: false };
    const cost = num(v.cost) ?? 0, packaging = num(v.packaging) ?? 0, shipping = num(v.shipping) ?? 0;
    const ads = num(v.ads) ?? 0, gstP = num(v.gst) ?? 0, other = num(v.other) ?? 0;
    const orders = num(v.orders) ?? 1;
    const feePct = v.feeMode === "pct" ? (num(v.feePct) ?? 0) : 0;
    const feeAmt = v.feeMode === "pct" ? price * feePct / 100 : (num(v.feeFixed) ?? 0);
    const gstAmt = price * gstP / 100;
    const base = cost + packaging + shipping;
    const expenses = base + feeAmt + ads + gstAmt + other;
    const profit = price - expenses;
    const margin = price > 0 ? profit / price * 100 : 0;
    const costPct = price > 0 ? expenses / price * 100 : 0;
    const fixed = base + ads + other + (v.feeMode === "fixed" ? feeAmt : 0);
    const keep = 1 - (feePct + gstP) / 100;
    const breakEven = keep > 1e-9 ? fixed / keep : null;
    const target = num(v.target);
    const required = target !== null && keep > 1e-9 ? (fixed + target) / keep : null;
    return {
      ready: true, price, feeAmt, gstAmt, base, expenses, profit, margin, costPct,
      breakEven, keep, target, required, ads, other, orders,
      totalRevenue: price * orders, totalExpenses: expenses * orders, totalProfit: profit * orders,
    };
  }, [v, errors]);

  const rounded = r.ready ? (Math.round(r.profit * 100) / 100 || 0) : 0;
  const state = !r.ready ? "idle" : rounded > 0 ? "good" : rounded < 0 ? "bad" : "zero";
  const bannerCls = {
    idle: "bg-blue-50 text-blue-700 border-blue-200",
    good: "bg-emerald-50 text-emerald-700 border-emerald-200",
    bad: "bg-red-50 text-red-700 border-red-200",
    zero: "bg-amber-50 text-amber-700 border-amber-200",
  }[state];
  const bannerMsg = {
    idle: "Enter your numbers to see your profit.",
    good: `You earn ${inr(rounded)} per order`,
    bad: `You are losing ${inr(Math.abs(rounded))} per order`,
    zero: "You break even at this price.",
  }[state];

  const summary = () => [
    "E-commerce Profit Calculator — https://ecroplyai.in/profit-calculator",
    `Selling price: ${inr(r.price)} | Total expenses: ${inr(r.expenses)}`,
    `Net profit per order: ${inr(r.profit)} (${pct(r.margin)} margin)`,
    r.breakEven !== null ? `Break-even price: ${inr(r.breakEven)}` : "Break-even price: not possible at 100%+ fees",
    `For ${r.orders} order(s): revenue ${inr(r.totalRevenue)}, expenses ${inr(r.totalExpenses)}, profit ${inr(r.totalProfit)}`,
    r.required !== null ? `Required price for ${inr(r.target)} profit/order: ${inr(r.required)}` : null,
  ].filter(Boolean).join("\n");

  const copyResults = async () => {
    if (!r.ready) { toast.error("Enter valid numbers first."); return; }
    await navigator.clipboard.writeText(summary());
    toast.success("Results copied to clipboard!");
  };

  const shareResults = async () => {
    if (!r.ready) { toast.error("Enter valid numbers first."); return; }
    const payload = { title: "E-commerce Profit Calculator", text: summary(), url: "https://ecroplyai.in/profit-calculator" };
    if (navigator.share) {
      try { await navigator.share(payload); } catch { /* user cancelled */ }
    } else {
      await navigator.clipboard.writeText(summary());
      toast.success("Sharing not supported here — results copied instead!");
    }
  };

  const feeToggle = (mode) => set("feeMode", mode);
  const segBtn = (active) => `px-4 h-11 text-sm font-semibold transition-colors ${active ? "bg-blue-600 text-white" : "text-slate-500 hover:text-slate-800"}`;

  return (
    <>
      <Seo
        title="E-commerce Profit Calculator – Calculate Selling Profit | Ecroply AI"
        description="Free e-commerce profit calculator to calculate product profit, marketplace fees, shipping, advertising costs, GST, break-even price and profit margin."
        path="/profit-calculator"
        jsonLd={JSONLD}
        breadcrumb={[{ name: "Home", path: "/" }, { name: "Ecommerce Tools", path: "/ecommerce-tools" }, { name: "Profit Calculator", path: "/profit-calculator" }]}
      />

      <section className="relative overflow-hidden bg-slate-900">
        <div className="absolute inset-0 grid-dots opacity-10" />
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12">
          <nav className="flex items-center gap-2 text-xs text-slate-400 mb-6" data-testid="breadcrumb">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/ecommerce-tools" className="hover:text-white">Ecommerce Tools</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-slate-200">Profit Calculator</span>
          </nav>
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur flex items-center justify-center shrink-0">
              <Calculator className="w-7 h-7 text-blue-300" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">E-commerce Profit Calculator</h1>
              <p className="mt-3 text-base md:text-lg text-slate-300 max-w-2xl leading-relaxed">Calculate your real profit before you sell.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="relative -mt-6 pb-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-6 lg:grid-cols-2 items-start">
          {/* INPUTS */}
          <div className="bg-white border border-slate-200 rounded-3xl p-5 sm:p-7 shadow-xl shadow-slate-200/50" data-testid="profit-inputs">
            <h2 className="text-lg font-bold text-slate-900 mb-4">Your numbers</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Selling Price (₹)" name="price" value={v.price} onChange={set} prefix="₹" error={errors.price} testid="profit-input-price" />
              <Field label="Product Cost (₹)" name="cost" value={v.cost} onChange={set} prefix="₹" error={errors.cost} testid="profit-input-cost" />
              <Field label="Packaging Cost (₹)" name="packaging" value={v.packaging} onChange={set} prefix="₹" error={errors.packaging} testid="profit-input-packaging" />
              <Field label="Shipping Cost (₹)" name="shipping" value={v.shipping} onChange={set} prefix="₹" error={errors.shipping} testid="profit-input-shipping" />

              <div className="sm:col-span-2">
                <span className="block text-sm font-semibold text-slate-800 mb-1.5">Marketplace fee type</span>
                <div className="inline-flex rounded-xl border border-slate-200 overflow-hidden" role="group" aria-label="Marketplace fee type">
                  <button type="button" onClick={() => feeToggle("pct")} aria-pressed={v.feeMode === "pct"} className={segBtn(v.feeMode === "pct")} data-testid="profit-fee-pct">Fee in %</button>
                  <button type="button" onClick={() => feeToggle("fixed")} aria-pressed={v.feeMode === "fixed"} className={segBtn(v.feeMode === "fixed")} data-testid="profit-fee-fixed">Fixed fee in ₹</button>
                </div>
              </div>

              {v.feeMode === "pct" ? (
                <Field label="Marketplace Fee (%)" name="feePct" value={v.feePct} onChange={set} suffix="%" error={errors.feePct} testid="profit-input-feepct" />
              ) : (
                <Field label="Marketplace Fee (₹)" name="feeFixed" value={v.feeFixed} onChange={set} prefix="₹" error={errors.feeFixed} testid="profit-input-feefixed" />
              )}
              <Field label="Advertising Cost (₹)" name="ads" value={v.ads} onChange={set} prefix="₹" error={errors.ads} testid="profit-input-ads" />
              <Field label="GST / Tax (%)" name="gst" value={v.gst} onChange={set} suffix="%" error={errors.gst} testid="profit-input-gst" />
              <Field label="Other Costs (₹)" name="other" value={v.other} onChange={set} prefix="₹" error={errors.other} testid="profit-input-other" />
              <Field label="Number of Orders" name="orders" value={v.orders} onChange={set} error={errors.orders} testid="profit-input-orders" step="1" inputMode="numeric" />
            </div>

            <button type="button" onClick={() => setV(DEFAULTS)} data-testid="profit-btn-reset"
              className="mt-5 inline-flex items-center gap-2 h-11 px-4 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
              <RotateCcw className="w-4 h-4" /> Reset
            </button>

            <div className="mt-6 pt-6 border-t border-slate-200">
              <h2 className="text-lg font-bold text-slate-900 mb-1">Target Profit Calculator <span className="font-normal text-slate-400 text-sm">(optional)</span></h2>
              <Field label="Desired Profit per Order (₹)" name="target" value={v.target} onChange={set} prefix="₹" error={errors.target} testid="profit-input-target" placeholder="e.g. 100" />
              {r.ready && r.required !== null && (
                <div className="mt-3 rounded-xl bg-blue-50 border border-blue-200 p-4" data-testid="profit-target-out">
                  <p className="text-sm text-slate-600">Required selling price for {inr(r.target)} profit per order</p>
                  <p className="text-2xl font-extrabold text-blue-700 mt-1">{inr(r.required)}</p>
                </div>
              )}
              {r.ready && r.target !== null && r.keep <= 1e-9 && (
                <p className="mt-3 text-sm text-red-600">Not possible: percentage fees and tax total 100% or more.</p>
              )}
            </div>
          </div>

          {/* RESULTS */}
          <div className="lg:sticky lg:top-20 bg-white border border-slate-200 rounded-3xl p-5 sm:p-7 shadow-xl shadow-slate-200/50" data-testid="profit-results" aria-live="polite">
            <h2 className="text-lg font-bold text-slate-900 mb-4">Results</h2>
            <div className={`rounded-xl border px-4 py-3 font-bold text-base mb-4 ${bannerCls}`} data-testid="profit-banner">{bannerMsg}</div>

            <div className="grid grid-cols-2 gap-3 mb-5">
              {[
                { icon: IndianRupee, k: "Net Profit", val: r.ready ? inr(rounded) : "–", testid: "profit-v-net", cls: state === "good" ? "text-emerald-600" : state === "bad" ? "text-red-600" : "text-slate-900" },
                { icon: ChartPie, k: "Profit Margin", val: r.ready ? pct(r.margin) : "–", testid: "profit-v-margin", cls: "text-slate-900" },
                { icon: Package, k: "Total Cost", val: r.ready ? pct(r.costPct) : "–", testid: "profit-v-costpct", cls: "text-slate-900" },
                { icon: Target, k: "Break-even Price", val: r.ready ? (r.breakEven !== null ? inr(r.breakEven) : "Not possible") : "–", testid: "profit-v-breakeven", cls: "text-slate-900" },
              ].map((c) => (
                <div key={c.k} className="border border-slate-200 rounded-2xl p-4 bg-slate-50/60">
                  <div className="flex items-center gap-1.5 text-xs text-slate-500 font-medium"><c.icon className="w-3.5 h-3.5" /> {c.k}</div>
                  <div className={`mt-1.5 text-xl sm:text-2xl font-extrabold break-words ${c.cls}`} data-testid={c.testid}>{c.val}</div>
                </div>
              ))}
            </div>

            <h3 className="text-sm font-bold text-slate-900 mb-2">Per order</h3>
            <table className="w-full text-sm mb-5" data-testid="profit-table-order">
              <tbody>
                {[
                  ["Selling Price", r.ready ? inr(r.price) : "–"],
                  ["Product + packaging + shipping", r.ready ? inr(r.base) : "–"],
                  ["Marketplace fee", r.ready ? inr(r.feeAmt) : "–"],
                  ["Advertising", r.ready ? inr(r.ads) : "–"],
                  ["GST / Tax", r.ready ? inr(r.gstAmt) : "–"],
                  ["Other costs", r.ready ? inr(r.other) : "–"],
                ].map(([k, val]) => (
                  <tr key={k} className="border-b border-slate-100"><td className="py-2 text-slate-600">{k}</td><td className="py-2 text-right font-medium text-slate-900 tabular-nums">{val}</td></tr>
                ))}
                <tr className="border-b border-slate-200"><td className="py-2 font-bold text-slate-900">Total Expenses</td><td className="py-2 text-right font-bold text-slate-900 tabular-nums" data-testid="profit-t-exp">{r.ready ? inr(r.expenses) : "–"}</td></tr>
                <tr><td className="py-2 font-bold text-slate-900">Net Profit</td><td className={`py-2 text-right font-bold tabular-nums ${state === "good" ? "text-emerald-600" : state === "bad" ? "text-red-600" : "text-slate-900"}`} data-testid="profit-t-profit">{r.ready ? inr(rounded) : "–"}</td></tr>
              </tbody>
            </table>

            <h3 className="text-sm font-bold text-slate-900 mb-2">For <span data-testid="profit-n-orders">{r.ready ? r.orders : "–"}</span> order(s)</h3>
            <table className="w-full text-sm mb-5" data-testid="profit-table-multi">
              <tbody>
                <tr className="border-b border-slate-100"><td className="py-2 text-slate-600">Total Revenue</td><td className="py-2 text-right font-medium text-slate-900 tabular-nums" data-testid="profit-m-rev">{r.ready ? inr(r.totalRevenue) : "–"}</td></tr>
                <tr className="border-b border-slate-100"><td className="py-2 text-slate-600">Total Expenses</td><td className="py-2 text-right font-medium text-slate-900 tabular-nums" data-testid="profit-m-exp">{r.ready ? inr(r.totalExpenses) : "–"}</td></tr>
                <tr><td className="py-2 font-bold text-slate-900">Total Profit</td><td className={`py-2 text-right font-bold tabular-nums ${state === "good" ? "text-emerald-600" : state === "bad" ? "text-red-600" : "text-slate-900"}`} data-testid="profit-m-profit">{r.ready ? inr(rounded * r.orders) : "–"}</td></tr>
              </tbody>
            </table>

            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={copyResults} data-testid="profit-btn-copy"
                className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold shadow-lg shadow-blue-500/25 transition-colors">
                <Copy className="w-4 h-4" /> Copy results
              </button>
              <button type="button" onClick={shareResults} data-testid="profit-btn-share"
                className="inline-flex items-center gap-2 h-11 px-4 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
                <Share2 className="w-4 h-4" /> Share results
              </button>
            </div>

            <details className="mt-5 text-sm">
              <summary className="cursor-pointer font-semibold text-slate-700" data-testid="profit-formulas-toggle">How it's calculated</summary>
              <div className="mt-2 space-y-1.5 text-xs text-slate-500">
                <p>Marketplace Fee = Selling Price × Fee % ÷ 100 (or the fixed ₹ fee)</p>
                <p>GST/Tax = Selling Price × GST % ÷ 100</p>
                <p>Total Expenses = Product + Packaging + Shipping + Fee + Ads + GST/Tax + Other</p>
                <p>Net Profit = Selling Price − Total Expenses</p>
                <p>Profit Margin % = Net Profit ÷ Selling Price × 100</p>
                <p>Break-even Price = Fixed costs ÷ (1 − (Fee % + GST %) ÷ 100)</p>
                <p>Required Price = (Fixed costs + Target Profit) ÷ (1 − (Fee % + GST %) ÷ 100)</p>
              </div>
            </details>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8"><AdUnit /></div>
      </section>

      {/* SEO CONTENT */}
      <article className="bg-white border-t border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-10">
          <section>
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight mb-3">What is an e-commerce profit calculator?</h2>
            <p className="text-base text-slate-600 leading-relaxed">An e-commerce profit calculator shows how much money you actually keep from each sale after every cost is paid. A selling price of ₹299 can look healthy until product cost, shipping, marketplace commission, ads and tax are taken out. This tool does that subtraction for you, instantly, so you can check a price before you list a product.</p>
          </section>
          <section>
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight mb-3">How to calculate product profit</h2>
            <p className="text-base text-slate-600 leading-relaxed">Start with the selling price and subtract every cost tied to that single order. In simple terms:</p>
            <p className="mt-3 text-base text-slate-800 font-semibold bg-slate-50 border border-slate-200 rounded-xl p-4">Net Profit = Selling Price − (Product Cost + Packaging + Shipping + Marketplace Fee + Advertising + GST/Tax + Other Costs)</p>
            <p className="mt-3 text-base text-slate-600 leading-relaxed">Percentage-based costs, such as a 10% marketplace fee, are calculated on the selling price. Fixed costs, such as packaging, stay the same on every order. Once you know profit per order, multiply by the number of orders to see your total profit for a day, week or campaign.</p>
          </section>
          <section>
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight mb-3">What costs should sellers include?</h2>
            <ul className="space-y-2 text-base text-slate-600 leading-relaxed list-disc pl-5">
              <li><strong className="text-slate-800">Product cost:</strong> what you pay to buy or make one unit.</li>
              <li><strong className="text-slate-800">Packaging:</strong> boxes, mailers, tape, inserts and labels.</li>
              <li><strong className="text-slate-800">Shipping:</strong> courier charges, including weight slabs and return-to-origin costs if they apply to you.</li>
              <li><strong className="text-slate-800">Marketplace fees:</strong> commission, fixed closing fees and payment-collection charges.</li>
              <li><strong className="text-slate-800">Advertising:</strong> ad spend divided by the orders it produced.</li>
              <li><strong className="text-slate-800">GST / tax:</strong> any tax you pay out of your own margin.</li>
              <li><strong className="text-slate-800">Other costs:</strong> software, storage, staff time, returns and damages.</li>
            </ul>
          </section>
          <section>
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight mb-3">What is profit margin?</h2>
            <p className="text-base text-slate-600 leading-relaxed">Profit margin is your profit shown as a percentage of the selling price. A ₹60 profit on a ₹300 sale is a 20% margin. Margin makes it easy to compare products at different price points and to spot items that look busy but earn very little.</p>
          </section>
          <section>
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight mb-3">What is break-even price?</h2>
            <p className="text-base text-slate-600 leading-relaxed">Your break-even price is the lowest selling price at which you neither make nor lose money. Because fees and tax grow as the price grows, the answer is not simply your total costs. The calculator works it out by dividing your fixed costs by what is left of each rupee after percentage-based charges. Selling below this price means you lose money on every order.</p>
          </section>
          <section className="bg-blue-50 border border-blue-200 rounded-2xl p-5">
            <p className="text-sm text-slate-700 leading-relaxed">
              Pricing sorted? Crop your shipping labels with the{" "}
              <Link to="/tools/pdf-shipping-label-cropper" className="text-blue-700 font-semibold hover:underline">PDF Shipping Label Cropper</Link>{" "}
              or write your listing with our free{" "}
              <Link to="/ai-tools" className="text-blue-700 font-semibold hover:underline">AI listing tools</Link>.
            </p>
          </section>
        </div>
      </article>
    </>
  );
}
